import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

class VibeStickyAtc extends Component {
  #observer = null;
  #endObserver = null;
  #pastBuyBox = false;
  #reachedEnd = false;

  connectedCallback() {
    super.connectedCallback();

    // Portal to <body> so position:fixed resolves to the viewport and z-index
    // wins globally. Nested inside product-details, a transformed/isolated
    // ancestor traps the fixed bar in a local stacking context, letting later
    // sections (e.g. the lifestyle gallery) paint over it (Juuso 22.7.).
    if (this.parentElement !== document.body) {
      document.body.appendChild(this);
      return; // reconnect fires and runs the setup below with body as parent
    }

    const target =
      document.querySelector('product-form-component') ||
      document.querySelector('.product-information');

    if (target) {
      this.#observer = new IntersectionObserver(
        ([entry]) => {
          this.#pastBuyBox = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          this.#update();
        },
        { threshold: 0 }
      );
      this.#observer.observe(target);
    }

    // Hide once the footer (end of the page) comes into view, so the bar
    // doesn't float over the footer / recommendations (Timo 22.7.).
    const end = document.querySelector('.shopify-section-group-footer-group');
    if (end) {
      this.#endObserver = new IntersectionObserver(
        ([entry]) => {
          this.#reachedEnd = entry.isIntersecting;
          this.#update();
        },
        { threshold: 0 }
      );
      this.#endObserver.observe(end);
    }

    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.#endObserver?.disconnect();
  }

  #update() {
    this.#setVisible(this.#pastBuyBox && !this.#reachedEnd);
  }

  #setVisible(visible) {
    this.classList.toggle('is-visible', visible);
    this.setAttribute('aria-hidden', String(!visible));
  }

  #onClick = (event) => {
    if (event.target.closest?.('[data-vibe-sticky-add]')) this.#addToCart();
  };

  #liveVariantId() {
    const input = document.querySelector('product-form-component form input[name="id"]');
    return input?.value || this.dataset.variantId;
  }

  async #addToCart() {
    const button = this.querySelector('[data-vibe-sticky-add]');
    const variantId = this.#liveVariantId();
    if (!variantId || button?.disabled) return;

    if (button) button.disabled = true;

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');

    const sectionIds = [];
    for (const item of document.querySelectorAll('cart-items-component')) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionIds.push(item.dataset.sectionId);
      }
    }
    if (sectionIds.length > 0) formData.append('sections', sectionIds.join(','));

    const deferredEventPromise = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferredEventPromise.promise,
      })
    );

    const fetchCfg = fetchConfig('javascript', { body: formData });

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchCfg,
        headers: { ...fetchCfg.headers, Accept: 'text/html' },
      }).then((res) => res.json());

      if (response.status) {
        this.dispatchEvent(
          new CartErrorEvent({
            error: response.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: response.description, errors: response.errors },
          })
        );
      }

      const ajaxCart = await this.#refreshCart();
      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          didError: Boolean(response.status),
          items: ajaxCart.items,
          source: 'vibe-sticky-atc',
          sourceId: this.id.toString(),
          itemCount: 1,
          productId: this.dataset.productId,
          sections: response.sections,
        },
      });
    } catch (error) {
      deferredEventPromise.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: error?.message || 'Network error during add to cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
    } finally {
      if (button) button.disabled = false;
    }
  }

  async #refreshCart() {
    const cartItemsComponent = document.querySelector('cart-items-component');
    if (cartItemsComponent) {
      await customElements.whenDefined('cart-items-component');
      return cartItemsComponent.fetchCartData();
    }

    return fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then((response) => {
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
      return response.json();
    });
  }
}

if (!customElements.get('vibe-sticky-atc')) {
  customElements.define('vibe-sticky-atc', VibeStickyAtc);
}
